import { Component, Inject, OnInit } from "@angular/core";
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { AdaptiveAccessNodeRuleset, Header, Node } from "@aptsi-types";
import { ActivatedRoute, Router } from "@angular/router";
import { AdaptiveAccessNodeRulesetService } from "./adaptive-access-node-ruleset.service";
import { RulesetService } from "../../ruleset/ruleset.service";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { ErrorDialogComponent } from "../../shared/dialogs/error-dialog.component";
import { Ruleset } from "@aptsi-types";
import { SuccessDialogComponent } from "../../shared/dialogs/success-dialog/success-dialog.component";
import { NodeService } from "../../ruleset/node/node.service";
import { MatDialogRef } from "@angular/material/dialog";
import { EnvironmentService } from "src/app/core/service/environment.service";
@Component({
  selector: "app-access-flow-ruleset-create-or-update",
  templateUrl: "./adaptive-access-node-ruleset-create-or-update.component.html",
  styleUrls: ["./adaptive-access-node-ruleset-create-or-update.component.scss"],
})
export class AdaptiveAccessNodeRulesetCreateOrUpdateDialogComponent implements OnInit {
  // -------------------- UI/Modal Context --------------------
  isCreate = this.data.isCreate;
  showModal = false;

  // -------------------- Identifiers and Data Models --------------------
  existingAdaptiveAccessNodeId: string = null;
  adaptiveAccessNodeRuleset: AdaptiveAccessNodeRuleset = null;
  associatedRules: Node[] = [];
  rulesets: Ruleset[] = [];
  filteredDataToSearch: any[] = [];
  headers: Header[] = [
    { name: "Rule Id", property: "ruleId" },
    { name: "Rule Name", property: "ruleName" },
  ];
  readonly nullver = 1;
  readonly typeOptions = ["risk_score", "asset_access_threshold", "asset_access_determination", "None"];

  // -------------------- Form --------------------
  form: FormGroup;

  // -------------------- Constructor --------------------
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      message: string;
      digitalId: string;
      path: string;
      orgnization: string;
      parentid: string;
      pathoverride: string;
      isCreate: boolean;
      isCreateForUpdate: boolean;
      selectedEntries: any[];
      adaptiveAccessNodeId: string;
      adaptiveAccessNodeRulesetId: string;
    },
    private dialogRef: MatDialogRef<AdaptiveAccessNodeRulesetCreateOrUpdateDialogComponent>,
    private router: Router,
    private route: ActivatedRoute,
    private adaptiveAccessNodeRulesetService: AdaptiveAccessNodeRulesetService,
    private dialog: MatDialog,
    private ruleSetService: RulesetService,
    private nodeService: NodeService,
    public environmentService: EnvironmentService 
  ) {}

  // -------------------- Lifecycle --------------------
  async ngOnInit(): Promise<void> {
    if (!this.isCreate) {
      await this.fetchAdaptiveAccessNodeRulesets();
    }
    

    this.initForm();

    if (!this.isCreate) {
      await this.fetchNodeFirstTime();
    }
  
    await this.fetchRulesets();

 
    console.log("existing AdaptiveAccess NodeId for update", this.data.adaptiveAccessNodeId);
    this.filteredDataToSearch = this.rulesets.map(w => ({ text: w.name, value: w.rulesetid }));
  }

  // -------------------- Form Initialization --------------------
  private initForm(): void {
    if (this.isCreate) {
      this.form = new FormGroup({
        adaptiveAccessNodeId: new FormControl(this.data.adaptiveAccessNodeId),
        ruleSetId: new FormControl(null, Validators.required),
        ruleSetType: new FormControl("None", [Validators.required, this.typeValidator]),
      });
    } else {
      this.form = new FormGroup({
        adaptiveAccessNodeRulesetId: new FormControl(this.adaptiveAccessNodeRuleset?.adaptiveAccessNodeRulesetId ?? null, Validators.required),
        adaptiveAccessNodeId: new FormControl(this.data.adaptiveAccessNodeId, Validators.required),
        ruleSetId: new FormControl(this.adaptiveAccessNodeRuleset?.ruleSetId ?? null, Validators.required),
        ruleSetType: new FormControl(this.adaptiveAccessNodeRuleset?.ruleSetType ?? "None", [Validators.required, this.typeValidator]),
      });
    }
  }

  // -------------------- Validators --------------------
  typeValidator(control: AbstractControl): { [key: string]: any } | null {
    return control.value === "None" ? { noneSelected: true } : null;
  }

  // -------------------- Fetching Data --------------------
  async fetchAdaptiveAccessNodeRulesets(): Promise<void> {
    try {
      const response = await this.adaptiveAccessNodeRulesetService
        .getAdaptiveAccessNodeRuleset(this.data.adaptiveAccessNodeRulesetId)
        .toPromise();
      this.adaptiveAccessNodeRuleset = response["Resource retrieved successfully"]?.[0] ?? null;

      if (this.adaptiveAccessNodeRuleset) {
        this.form.patchValue({
          adaptiveAccessNodeRulesetId: this.adaptiveAccessNodeRuleset.adaptiveAccessNodeRulesetId,
          adaptiveAccessNodeId: this.adaptiveAccessNodeRuleset.adaptiveAccessNodeId,
          ruleSetId: this.adaptiveAccessNodeRuleset.ruleSetId,
          ruleSetType: this.adaptiveAccessNodeRuleset.ruleSetType,
        });
      }
    } catch (error) {
      console.error("Error fetching accessflows:", error);
    }
  }

  async fetchAdaptiveAccessNodeRuleset(): Promise<void> {
    try {
      const response = await this.adaptiveAccessNodeRulesetService
        .getAdaptiveAccessNodeRulesetByaccessNodeId(this.form.value.adaptiveAccessNodeId)
        .toPromise();
      this.adaptiveAccessNodeRuleset = response["Resource retrieved successfully"] ?? [];
    } catch (error) {
      console.error("Error fetching accessflows:", error);
    }
  }

  async fetchRulesets(): Promise<void> {
    try {
      const response = await this.ruleSetService.getAllRulesets(this.nullver).toPromise();
      this.rulesets = response["Resource retrieved successfully"] ?? [];
    } catch (error) {
      console.error("Error fetching rulesets:", error);
    }
  }

  async fetchNodeFirstTime(): Promise<void> {
    try {
      const ruleSetId = this.form.value.ruleSetId;
      if (!ruleSetId) return;
      const response = await this.ruleSetService.getNode(ruleSetId).toPromise();
      this.associatedRules = response["nodes"];
    } catch (error) {
      console.error("Error fetching rulesets:", error);
    }
  }

  async fetchNode(event: any): Promise<void> {
    try {
      const response = await this.ruleSetService.getNode(event.value).toPromise();
      this.associatedRules = response["nodes"];
    } catch (error) {
      console.error("Error fetching rulesets:", error);
    }
  }

  async checkRulesetIdExists(rulesetId: string): Promise<boolean> {
    try {
      const response = await this.ruleSetService.getRuleset(rulesetId).toPromise();
      const existing = response["Resource retrieved successfully."];
      return existing !== null && existing.length > 0;
    } catch (error) {
      console.error("Error checking rulesetId existence:", error);
      return false;
    }
  }

  // -------------------- Event Handlers --------------------
  lookup(searchValue: string): void {
    this.filteredDataToSearch = this.rulesets
      .filter(i => i.name.toLowerCase().includes(searchValue))
      .map(w => ({ text: w.name, value: w.rulesetid }));
  }

  clean(input: any): void {
    input.value = "";
    this.lookup("");
  }

  cancel(): void {
    this.dialogRef.close(true);
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  redirectToRuleSetCreate(): void {
    this.router.navigate(["ada-access/auth-tools/policy-authoring/ruleset/create"]);
    this.closeDialog();
  }

  gotToRuleset(): void {
    this.router.navigate(["ada-access/auth-tools/ruleset/create"]);
  }

  // -------------------- Submit Logic --------------------
  async submit(): Promise<void> {
    try {
      if (this.isCreate) {
        if (this.data.isCreateForUpdate) {
          await this.adaptiveAccessNodeRulesetService
            .createAdaptiveAccessNodeRulesetForUpdateScreen(
              this.form.value.adaptiveAccessNodeId,
              this.form.value.ruleSetId,
              this.form.value.ruleSetType
            )
            .toPromise();
        } else {
          await this.adaptiveAccessNodeRulesetService
            .createAdaptiveAccessNodeRuleset(
              this.form.value.adaptiveAccessNodeId,
              this.form.value.ruleSetId,
              this.form.value.ruleSetType
            )
            .toPromise();

          const selectedEntry = {
            ruleSetId: this.form.value.ruleSetId,
            ruleSetType: this.form.value.ruleSetType,
          };
          this.adaptiveAccessNodeRulesetService.addNodeRulesets(selectedEntry);
        }
        this.openSuccessDialog(true);
      } else {
        await this.adaptiveAccessNodeRulesetService
          .updateAdaptiveAccessNodeRuleset(
            this.form.value.adaptiveAccessNodeId,
            this.form.value.adaptiveAccessNodeRulesetId,
            this.form.value.ruleSetType,
            this.form.value.ruleSetId
          )
          .toPromise();
        this.openSuccessDialog(false);
        await this.fetchAdaptiveAccessNodeRuleset();
      }

      this.dialogRef.close(true);
    } catch (error) {
      this.openErrorDialog(error.message || "An unexpected error occurred.");
    }
  }

  // -------------------- Dialogs --------------------
  openSuccessDialog(isCreate: boolean): void {
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: "250px",
      data: {
        isCreate: isCreate,
        existingAdaptiveAccessNodeId: this.existingAdaptiveAccessNodeId,
        title: "Adaptive Access Node Ruleset",
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate([
        "ada-access/auth-tools/adaptiveaccessnode/update",
        this.existingAdaptiveAccessNodeId,
      ]);
    });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: "250px",
      data: { errorMessage },
    });
  }
}

